import { inject, Injectable } from '@angular/core';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { CookieService } from 'ngx-cookie-service';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class CookieHelperService {
  cookieService = inject(CookieService);
  _sharedService = inject(SharedService);
  private analyticsService = inject(AnalyticsService);

  setCookies(data: any, expiry: Date): void {
    const { token, organizationId, userDetail, gT_PackageId, gT_ProductId, sessionId } = data.data;

    this.cookieService.set('token', token, expiry, '/', '', true, 'Strict');
    this.cookieService.set('organizationId', organizationId, expiry);
    this.cookieService.set('userId', userDetail?.userId, expiry);
    this.cookieService.set('forceResetPasswordDisable', userDetail?.isFlag, expiry);
    this.cookieService.set('gT_PackageId', gT_PackageId, expiry);
    this.cookieService.set('gT_ProductId', gT_ProductId, expiry);
    this.cookieService.set('sessionId', sessionId, expiry);
    const userTypeHash = this._sharedService.encodeURIComponentParam(userDetail.role || '');
    this.cookieService.set('u_type', userTypeHash);

    // Identify the fresh login in GA4 (no-op outside PROD). Stable, non-PII ids.
    this.analyticsService.setUser(userDetail?.userId, organizationId);
  }

  updateTokenOnly(token: string, expiry: Date): void {
    this.cookieService.set('token', token, expiry, '/', '', true, 'Strict');
  }

  updateExpiry(expiry: Date): void {
    const cookiesToUpdate = [
      'organizationId',
      'userId',
      'forceResetPasswordDisable',
      'gT_PackageId',
      'gT_ProductId',
      'sessionId',
    ];
    cookiesToUpdate.forEach(cookie => {
      const value = this.cookieService.get(cookie);
      if (value) {
        this.cookieService.set(cookie, value, expiry);
      }
    });
  }

  getGlobalUpgradePopupPreference(): string {
    const localStoragePref = localStorage.getItem('globalHideUpgradePopup');
    if (localStoragePref !== null) {
      return localStoragePref;
    }

    const sessionStoragePref = sessionStorage.getItem('globalHideUpgradePopup');
    if (sessionStoragePref !== null) {
      localStorage.setItem('globalHideUpgradePopup', sessionStoragePref);
      return sessionStoragePref;
    }

    const cookiePref = this.getCookie('globalHideUpgradePopup');
    if (cookiePref) {
      localStorage.setItem('globalHideUpgradePopup', cookiePref);
      return cookiePref;
    }

    return 'false';
  }

  setGlobalUpgradePopupPreference(hide: boolean, expiryDate: Date): void {
    const value = hide.toString();

    localStorage.setItem('globalHideUpgradePopup', value);

    sessionStorage.setItem('globalHideUpgradePopup', value);

    this.setCookie('globalHideUpgradePopup', value, expiryDate);

    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'globalHideUpgradePopup',
        newValue: value,
        url: window.location.href,
      })
    );
  }

  private setCookie(name: string, value: string, expiryDate: Date): void {
    const expires = `expires=${expiryDate.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  }

  public getCookie(name: string): string {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return '';
  }

  clearGlobalUpgradePopupPreference(): void {
    localStorage.removeItem('globalHideUpgradePopup');
    sessionStorage.removeItem('globalHideUpgradePopup');
    this.setCookie('globalHideUpgradePopup', '', new Date(0));

    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'globalHideUpgradePopup',
        newValue: null,
        url: window.location.href,
      })
    );
  }
}
